import React from "react";
import {
  Box,
  HStack,
  VStack,
  Avatar,
  Text,
  Tooltip,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  IconButton,
} from "@chakra-ui/react";
import { formatDistance } from "date-fns";
import { RiEdit2Line, RiDeleteBin6Line, RiMore2Line } from "react-icons/ri";
import { formatUtcDateReadableLong } from "../../../../utilities/dateUtils";
import { CommentDisplay, TruncatedCommentDisplay } from "./CommentDisplay";
import { Comment } from "../../../../shared/_core/comments.types";

interface CommentCardProps {
  comment: Comment;
  isEditing: boolean;
  editedContent: string | null;
  isCommentAuthor: boolean;
  canEditComment: boolean;
  canDeleteComment: boolean;
  isCommentsUpdating: boolean;
  isSaving: boolean;
  editorRef: React.MutableRefObject<any>;
  onEdit: (comment: Comment) => void;
  onDelete: (commentID: string) => void;
  onSave: (commentID: string) => void;
  onCancel: () => void;
  onContentChange: (content: string) => void;
  onSectionLinkClick: (sectionId: string) => void;
}

const CommentCard: React.FC<CommentCardProps> = ({
  comment,
  isEditing,
  editedContent,
  isCommentAuthor,
  canEditComment,
  canDeleteComment,
  isCommentsUpdating,
  isSaving,
  editorRef,
  onEdit,
  onDelete,
  onSave,
  onCancel,
  onContentChange,
  onSectionLinkClick,
}) => {
  const { id, user, content, modifiedTs, createTs } = comment;
  const { label: userLabel } = user;

  return (
    <Box
      bg="white"
      p={4}
      borderRadius="lg"
      boxShadow={isEditing ? "md" : "sm"}
      borderWidth="1px"
      borderColor={isEditing ? "blue.200" : "gray.200"}
      w="full"
      opacity={isCommentsUpdating ? 0.6 : 1}
      pointerEvents={isCommentsUpdating ? "none" : "auto"}
      transition="all 0.2s">
      <HStack spacing={3} align="start" w="full" mb={3}>
        <Avatar size="sm" name={userLabel} />
        <VStack align="flex-start" w="full" spacing={0}>
          <HStack justify="space-between" w="full">
            <VStack align="flex-start" spacing={0}>
              <Text fontWeight="600" fontSize="sm" color="gray.800">
                {userLabel}
              </Text>
              <Tooltip
                label={
                  <div>
                    Created on {formatUtcDateReadableLong(new Date(createTs))}
                    {modifiedTs && modifiedTs !== createTs && (
                      <div>
                        Last Modified on{" "}
                        {formatUtcDateReadableLong(new Date(modifiedTs))}
                      </div>
                    )}
                  </div>
                }>
                <HStack spacing={1}>
                  <Text fontSize="xs" color="gray.500">
                    {createTs
                      ? `${formatDistance(new Date(createTs), new Date())} ago`
                      : ""}
                  </Text>
                  {modifiedTs && modifiedTs !== createTs && (
                    <Text fontSize="xs" color="gray.400">
                      (edited)
                    </Text>
                  )}
                </HStack>
              </Tooltip>
            </VStack>

            {!isEditing && (canEditComment || canDeleteComment) && (
              <Menu size="sm">
                <MenuButton
                alignSelf="start"
                  as={IconButton}
                  size="xs"
                  variant="ghost"
                  icon={<RiMore2Line />}
                  aria-label="Comment options"
                  color="gray.400"
                  _hover={{ bg: "gray.100", color: "gray.600" }}
                  isDisabled={isCommentsUpdating}
                />
                <MenuList>
                  {canEditComment && (
                    <MenuItem
                      icon={<RiEdit2Line />}
                      onClick={() => onEdit(comment)}>
                      Edit Comment
                    </MenuItem>
                  )}
                  {canDeleteComment && (
                    <MenuItem
                      icon={<RiDeleteBin6Line />}
                      onClick={() => onDelete(id)}
                      color="red.500"
                      _hover={{ bg: "red.50" }}>
                      Delete Comment
                    </MenuItem>
                  )}
                </MenuList>
              </Menu>
            )}
          </HStack>
        </VStack>
      </HStack>

      <Box w="full">
        {isEditing ? (
          <CommentDisplay
            content={editedContent || content}
            isEditing
            editorRef={editorRef}
            onContentChange={onContentChange}
            onCancel={onCancel}
            onSave={() => onSave(id)}
            isSaving={isSaving}
          />
        ) : (
          <TruncatedCommentDisplay
            content={content}
            onSectionLinkClick={onSectionLinkClick}
            isEditing={false}
          />
        )}
      </Box>
    </Box>
  );
};

export default CommentCard;
